/**
 * Server-Side Property Scene Muxer Hook
 * Uses FFmpeg API service instead of browser WASM
 * Handles muxing (combining video + audio) for property video scenes
 */

import { useCallback, useReducer, useRef } from 'react';
import { useAction, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type { MuxingStatus } from '@/@types/ffmpeg';
import type {
  IPropertySceneMuxerState,
  IUsePropertySceneMuxerReturn,
} from '@/@types/property';

// ============================================
// State Management
// ============================================

type PropertySceneMuxerAction =
  | { type: 'START' }
  | { type: 'SET_STATUS'; status: MuxingStatus }
  | { type: 'SET_PROGRESS'; progress: number }
  | { type: 'SUCCESS'; storageId: Id<'_storage'> }
  | { type: 'ERROR'; error: string }
  | { type: 'RESET' };

const initialState: IPropertySceneMuxerState = {
  status: 'idle',
  progress: 0,
  error: null,
  resultStorageId: null,
};

function reducer(
  state: IPropertySceneMuxerState,
  action: PropertySceneMuxerAction
): IPropertySceneMuxerState {
  switch (action.type) {
    case 'START':
      return { ...initialState, status: 'downloading' };
    case 'SET_STATUS':
      return { ...state, status: action.status };
    case 'SET_PROGRESS':
      return { ...state, progress: action.progress };
    case 'SUCCESS':
      return {
        ...state,
        status: 'completed',
        progress: 100,
        resultStorageId: action.storageId,
      };
    case 'ERROR':
      return { ...state, status: 'error', error: action.error };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// ============================================
// Hook Implementation
// ============================================

export function useServerPropertySceneMuxer(): IUsePropertySceneMuxerReturn {
  const [state, dispatch] = useReducer(reducer, initialState);
  const abortedRef = useRef(false);

  // Convex actions for server-side muxing
  const muxVideoAudio = useAction(api.ffmpegApi.muxVideoAudio);
  const transferToStorage = useAction(api.ffmpegApi.transferToStorage);

  // Convex mutations for property scenes
  const updateMuxedStarted = useMutation(
    api.property.mutations.updatePropertySceneMuxStarted
  );
  const updateMuxedCompleted = useMutation(
    api.property.mutations.updatePropertySceneMuxCompleted
  );
  const updateMuxedFailed = useMutation(
    api.property.mutations.updatePropertySceneMuxFailed
  );

  const muxPropertyScene = useCallback(
    async (
      sceneId: Id<'propertyScenes'>,
      videoUrl: string,
      audioUrl: string
    ) => {
      abortedRef.current = false;
      dispatch({ type: 'START' });

      try {
        // Step 1: Mark muxing as started in database (0-5%)
        console.warn(
          '[useServerPropertySceneMuxer] Marking muxing as started for scene:',
          sceneId
        );
        await updateMuxedStarted({ sceneId });
        dispatch({ type: 'SET_PROGRESS', progress: 5 });

        if (abortedRef.current) {
          throw new Error('Operation cancelled');
        }

        // Step 2: Call FFmpeg API to mux video and audio (5-80%)
        dispatch({ type: 'SET_STATUS', status: 'muxing' });
        console.warn(
          '[useServerPropertySceneMuxer] Calling FFmpeg API to mux video and audio...',
          { videoUrl, audioUrl }
        );

        const muxResult = await muxVideoAudio({
          videoUrl,
          audioUrl,
        });

        console.warn(
          '[useServerPropertySceneMuxer] FFmpeg API muxing completed:',
          {
            resultUrl: muxResult.resultUrl,
            fileSizeBytes: muxResult.fileSizeBytes,
            processingTimeMs: muxResult.processingTimeMs,
          }
        );

        dispatch({ type: 'SET_PROGRESS', progress: 80 });

        if (abortedRef.current) {
          throw new Error('Operation cancelled');
        }

        // Step 3: Transfer from R2 to Convex storage (server-side, bypasses CORS) (80-95%)
        dispatch({ type: 'SET_STATUS', status: 'uploading' });
        console.warn(
          '[useServerPropertySceneMuxer] Transferring muxed video from R2 to Convex storage (server-side)...'
        );

        const transferResult = await transferToStorage({
          sourceUrl: muxResult.resultUrl,
          contentType: 'video/mp4',
        });

        const storageId = transferResult.storageId as Id<'_storage'>;
        console.warn(
          '[useServerPropertySceneMuxer] Transfer complete, storageId:',
          storageId,
          'size:',
          transferResult.fileSizeBytes
        );

        dispatch({ type: 'SET_PROGRESS', progress: 95 });

        if (abortedRef.current) {
          throw new Error('Operation cancelled');
        }

        // Step 4: Update database with muxed video info (95-100%)
        console.warn(
          '[useServerPropertySceneMuxer] Updating database with muxed video info...'
        );
        await updateMuxedCompleted({
          sceneId,
          muxedVideoStorageId: storageId,
          muxedFileSizeBytes: muxResult.fileSizeBytes,
        });

        dispatch({ type: 'SUCCESS', storageId });
        console.warn(
          '[useServerPropertySceneMuxer] Muxing completed successfully for scene:',
          sceneId
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Server-side muxing failed';
        console.error('[useServerPropertySceneMuxer] Error:', message);
        dispatch({ type: 'ERROR', error: message });

        // Try to update database with error
        try {
          await updateMuxedFailed({ sceneId, errorMessage: message });
        } catch (updateErr) {
          console.error(
            '[useServerPropertySceneMuxer] Failed to update database with error:',
            updateErr
          );
        }

        throw err;
      }
    },
    [
      muxVideoAudio,
      transferToStorage,
      updateMuxedStarted,
      updateMuxedCompleted,
      updateMuxedFailed,
    ]
  );

  const abort = useCallback(() => {
    // Note: Cannot stop server-side job once submitted, but we can mark as aborted locally
    console.warn(
      '[useServerPropertySceneMuxer] Abort requested - local state will be updated'
    );
    abortedRef.current = true;
    dispatch({ type: 'ERROR', error: 'Operation cancelled' });
  }, []);

  const reset = useCallback(() => {
    console.warn('[useServerPropertySceneMuxer] Resetting state');
    abortedRef.current = false;
    dispatch({ type: 'RESET' });
  }, []);

  return {
    muxPropertyScene,
    state,
    abort,
    reset,
  };
}
